import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/Card';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ReferenceLine } from 'recharts';
interface EffortProductionScatterProps {
  data: any[];
  xAxisKey: string;
  yAxisKey: string;
  xAxisName: string;
  yAxisName: string;
  tooltipFormat?: (value: any, name: string) => any;
}
export function EffortProductionScatter({
  data,
  xAxisKey,
  yAxisKey,
  xAxisName,
  yAxisName,
  tooltipFormat
}: EffortProductionScatterProps) {
  // Calculate averages for reference lines
  const avgX = data.length > 0 ? data.reduce((sum, d) => sum + (d[xAxisKey] || 0), 0) / data.length : 0;
  const avgY = data.length > 0 ? data.reduce((sum, d) => sum + (d[yAxisKey] || 0), 0) / data.length : 0;
  return <Card noPadding className="h-full flex flex-col" data-id="element-14">
      <div className="p-6 pb-4" data-id="element-15">
        <CardHeader data-id="element-16">
          <CardTitle data-id="element-17">Effort vs. Production Correlation</CardTitle>
          <CardDescription data-id="element-18">
            Each dot represents a Loan Officer. Top-right quadrant = high effort
            & high production. Lines show team averages.
          </CardDescription>
        </CardHeader>
      </div>

      <div className="flex-1 px-6" style={{
      minHeight: '300px'
    }} data-id="element-19">
        <ResponsiveContainer width="100%" height="100%" data-id="element-20">
          <ScatterChart margin={{
          top: 10,
          right: 30,
          bottom: 30,
          left: 30
        }} data-id="element-21">
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-22" />
            <XAxis type="number" dataKey={xAxisKey} name={xAxisName} label={{
            value: xAxisName,
            position: 'bottom',
            offset: 0,
            style: {
              fontSize: 12,
              fill: '#64748b'
            }
          }} tick={{
            fontSize: 12,
            fill: '#64748b'
          }} stroke="#cbd5e1" data-id="element-23" />
            <YAxis type="number" dataKey={yAxisKey} name={yAxisName} label={{
            value: yAxisName,
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 12,
              fill: '#64748b'
            }
          }} tick={{
            fontSize: 12,
            fill: '#64748b'
          }} stroke="#cbd5e1" data-id="element-24" />
            <ZAxis range={[100, 400]} data-id="element-25" />

            {/* Reference lines for averages */}
            {avgX > 0 && <ReferenceLine x={avgX} stroke="#94a3b8" strokeDasharray="5 5" strokeWidth={1.5} label={{
            value: 'Avg Effort',
            position: 'top',
            fill: '#64748b',
            fontSize: 11
          }} data-id="element-26" />}
            {avgY > 0 && <ReferenceLine y={avgY} stroke="#94a3b8" strokeDasharray="5 5" strokeWidth={1.5} label={{
            value: 'Avg Production',
            position: 'right',
            fill: '#64748b',
            fontSize: 11
          }} data-id="element-27" />}

            <Tooltip cursor={{
            strokeDasharray: '3 3'
          }} content={({
            active,
            payload
          }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              return <div className="bg-white p-4 border border-slate-200 shadow-lg rounded-lg text-sm" data-id="element-29">
                      <p className="font-bold text-slate-900 mb-2 text-base" data-id="element-30">
                        {data.name}
                      </p>
                      <div className="space-y-1" data-id="element-31">
                        <p className="text-slate-600" data-id="element-32">
                          <span className="font-medium text-indigo-600" data-id="element-33">
                            {xAxisName}:
                          </span>{' '}
                          <span className="font-semibold text-slate-900" data-id="element-34">
                            {data[xAxisKey]?.toLocaleString() || 0}
                          </span>
                        </p>
                        <p className="text-slate-600" data-id="element-35">
                          <span className="font-medium text-emerald-600" data-id="element-36">
                            {yAxisName}:
                          </span>{' '}
                          <span className="font-semibold text-slate-900" data-id="element-37">
                            {data[yAxisKey]?.toLocaleString() || 0}
                          </span>
                        </p>
                        <p className="text-slate-600" data-id="element-38">
                          <span className="font-medium text-slate-600" data-id="element-39">
                            Team:
                          </span>{' '}
                          <span className="text-slate-900" data-id="element-40">{data.team}</span>
                        </p>
                        <p className="text-slate-600 pt-1 border-t border-slate-100 mt-2" data-id="element-41">
                          <span className="font-medium text-slate-600" data-id="element-42">
                            Efficiency:
                          </span>{' '}
                          <span className="font-bold text-emerald-600" data-id="element-43">
                            {data.efficiency}%
                          </span>
                        </p>
                      </div>
                    </div>;
            }
            return null;
          }} data-id="element-28" />
            <Scatter name="Loan Officers" data={data} fill="#6366f1" fillOpacity={0.7} stroke="#4f46e5" strokeWidth={2} data-id="element-44" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="px-6 pb-6 pt-4 border-t border-slate-100" data-id="element-45">
        <div className="flex items-center justify-between text-xs text-slate-500" data-id="element-46">
          <div className="flex items-center gap-4" data-id="element-47">
            <div className="flex items-center gap-2" data-id="element-48">
              <div className="w-3 h-3 rounded-full bg-indigo-500 border-2 border-indigo-600" data-id="element-49"></div>
              <span data-id="element-50">Loan Officer</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-51">
              <div className="w-8 h-0.5 bg-slate-400" style={{
              backgroundImage: 'repeating-linear-gradient(to right, #94a3b8 0, #94a3b8 5px, transparent 5px, transparent 10px)'
            }} data-id="element-52"></div>
              <span data-id="element-53">Team Average</span>
            </div>
          </div>
          <div className="text-slate-400" data-id="element-54">{data.length} Loan Officers</div>
        </div>
      </div>
    </Card>;
}